<template>
	<FormContainer
		class="feature-flags"
		title="Feature Flags">
		<div
			v-if="loading"
			class="feature-flags__loading">
			Loading...
		</div>
		<div
			v-else-if="flags.length === 0"
			class="feature-flags__empty">
			No feature flags defined.
		</div>
		<div
			v-else
			class="feature-flags__list">
			<div
				v-for="flag in flags"
				:key="flag.key"
				class="feature-flags__item">
				<div class="feature-flags__info">
					<span class="feature-flags__label">{{ flag.label }}</span>
					<span class="feature-flags__key">{{ flag.key }}</span>
				</div>
				<ToggleSwitch
					:disabled="toggling === flag.key"
					:model-value="flag.active"
					@update:model-value="(val: boolean) => toggleFlag(flag.key, val)" />
			</div>
		</div>
	</FormContainer>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import ToggleSwitch from 'primevue/toggleswitch'
import FormContainer from '@/components/FormContainer.vue'
import FeatureFlagApi, { type FeatureFlag } from '@/helpers/api/FeatureFlagApi'

const api = new FeatureFlagApi()

const props = defineProps<{
	userId: number
}>()

const flags = ref<FeatureFlag[]>([])
const loading = ref(false)
const toggling = ref<string | null>(null)

async function fetchFlags() {
	loading.value = true
	try {
		const response = await api.list(props.userId)
		flags.value = response.data
	} finally {
		loading.value = false
	}
}

async function toggleFlag(key: string, active: boolean) {
	toggling.value = key
	try {
		const response = await api.toggle(props.userId, { feature: key, active })
		const index = flags.value.findIndex((f) => f.key === key)
		if (index !== -1) {
			flags.value[index] = response.data
		}
	} finally {
		toggling.value = null
	}
}

watch(() => props.userId, fetchFlags, { immediate: true })
</script>

<style scoped lang="scss">
.feature-flags {
	max-width: unset;

	.feature-flags__loading,
	.feature-flags__empty {
		color: var(--p-text-muted-color);
		font-size: 14px;
		padding: 8px 0;
	}

	.feature-flags__list {
		display: flex;
		flex-direction: column;
		gap: 12px;

		.feature-flags__item {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 10px 14px;
			border-radius: 8px;
			background: var(--p-surface-900);
			border: 1px solid var(--p-surface-800);

			.feature-flags__info {
				display: flex;
				flex-direction: column;
				gap: 2px;

				.feature-flags__label {
					font-size: 14px;
					font-weight: 600;
					color: var(--p-text-color);
				}

				.feature-flags__key {
					font-size: 12px;
					color: var(--p-text-muted-color);
					font-family: monospace;
				}
			}
		}
	}
}
</style>
